import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { navegarComCtrl } from "../../utils/solicitacoes/navegacao";
import { CustomTable } from "../../componentes/table/CustomTable";
import { CustomDropdown } from "../../componentes/dropdown/CustomDropdown";
import StatusBadge from "../../components/solicitacoes/Shared/StatusBadge";
import GraficosBasicos from "../../components/solicitacoes/Dashboard/GraficosBasicos";
import DashboardProcessoLayout, { Campo, INPUT_CLASS } from "../../components/solicitacoes/Dashboard/DashboardProcessoLayout";
import { useDashboardProcesso } from "../../hooks/solicitacoes/useDashboardProcesso";
import AnaliseLimitePrazoService from "../../services/resource/analiseLimitePrazoService";
import UsuarioService from "../../services/resource/usuarioService";
import { STATUS_CONFIG } from "../../utils/solicitacoes/statusConfig";
import { formatarMoeda } from "../../utils/solicitacoes/formatters";
import { kpisBasicos } from "../../utils/solicitacoes/chartOptions";

const service = new AnaliseLimitePrazoService();
const usuarioService = new UsuarioService();

const STATUS_OPTIONS = Object.entries(STATUS_CONFIG).map(([value, info]) => ({ value, label: info.label }));

const TIPO_SOLICITACAO_OPTIONS = [
    { value: "LIMITE", label: "Limite" }, { value: "PRAZO", label: "Prazo" }, { value: "AMBOS", label: "Ambos" },
];
const TIPO_SOLICITACAO_LABELS = { LIMITE: "Limite", PRAZO: "Prazo", AMBOS: "Ambos" };

const FILTROS_INICIAIS_EXTRA = {
    filialNome: "", tipoSolicitacao: null, supervisor: "", clienteNome: "", clienteCodigo: "", status: null, solicitanteId: null,
};

const COLUNAS = [
    { key: "filialNome", label: "Filial" },
    { key: "tipoSolicitacao", label: "Tipo da Solicitação", render: v => TIPO_SOLICITACAO_LABELS[v] || v },
    { key: "clienteCodigo", label: "Código Cliente" },
    { key: "clienteNome", label: "Cliente" },
    { key: "codigoRota", label: "Rota" },
    { key: "supervisor", label: "Supervisor" },
    { key: "limiteAtual", label: "Limite Atual", render: v => v != null ? formatarMoeda(v) : "-" },
    { key: "limiteProposto", label: "Limite Proposto", bold: true, render: v => v != null ? formatarMoeda(v) : "-" },
    { key: "limiteAprovado", label: "Limite Aprovado", render: v => v != null ? formatarMoeda(v) : "-" },
    { key: "prazoAtual", label: "Prazo Atual", render: v => v != null ? `${v} dias` : "-" },
    { key: "prazoProposto", label: "Prazo Proposto", render: v => v != null ? `${v} dias` : "-" },
    { key: "prazoAprovado", label: "Prazo Aprovado", render: v => v != null ? `${v} dias` : "-" },
    { key: "aprovacaoParcial", label: "Aprovação Parcial", render: v => v == null ? "-" : (v ? "Sim" : "Não"), center: true },
    { key: "status", label: "Status", render: v => <StatusBadge status={v} />, center: true },
    { key: "etapaAtual", label: "Etapa Atual" },
    { key: "solicitanteNome", label: "Solicitante" },
    { key: "dataSolicitacao", label: "Data", format: "date" },
];

export default function RelatorioAnaliseLimitePrazo() {
    const navigate = useNavigate();
    const [usuarios, setUsuarios] = useState([]);

    const { filtros, setFiltros, limparFiltros, pesquisar, linhas, dashboard, loading } = useDashboardProcesso({
        service, chaveFiltros: "solicitacoes.analiseLimitePrazo.filtros", filtrosIniciaisExtra: FILTROS_INICIAIS_EXTRA,
    });

    useEffect(() => {
        document.title = "MyDash | Análise de Limite e Prazo";
    }, []);

    useEffect(() => {
        usuarioService.listarUsuarios().then(res => setUsuarios(res.data)).catch(() => setUsuarios([]));
    }, []);

    function setFiltro(campo, valor) {
        setFiltros(prev => ({ ...prev, [campo]: valor }));
    }

    return (
        <DashboardProcessoLayout
            titulo="Análise de Limite e Prazo"
            onNovaSolicitacao={() => navigate("/solicitacoes/nova")}
            loading={loading}
            filtros={filtros}
            onFiltroChange={setFiltro}
            onPesquisar={pesquisar}
            onLimparFiltros={limparFiltros}
            kpis={kpisBasicos(dashboard, "fa fa-balance-scale")}
            graficos={<GraficosBasicos dashboard={dashboard} />}
            filtrosExtras={(
                <>
                    <Campo label="Filial">
                        <input type="text" value={filtros.filialNome} onChange={e => setFiltro("filialNome", e.target.value)} className={INPUT_CLASS} />
                    </Campo>
                    <Campo label="Tipo da Solicitação">
                        <CustomDropdown value={filtros.tipoSolicitacao} onChange={e => setFiltro("tipoSolicitacao", e.value)} options={TIPO_SOLICITACAO_OPTIONS}
                            placeholder="Todos" showClear />
                    </Campo>
                    <Campo label="Supervisor">
                        <input type="text" value={filtros.supervisor} onChange={e => setFiltro("supervisor", e.target.value)} className={INPUT_CLASS} />
                    </Campo>
                    <Campo label="Cliente">
                        <input type="text" value={filtros.clienteNome} onChange={e => setFiltro("clienteNome", e.target.value)}
                            placeholder="Razão social ou fantasia" className={INPUT_CLASS} />
                    </Campo>
                    <Campo label="Código Cliente">
                        <input type="text" value={filtros.clienteCodigo} onChange={e => setFiltro("clienteCodigo", e.target.value)} className={INPUT_CLASS} />
                    </Campo>
                    <Campo label="Status">
                        <CustomDropdown value={filtros.status} onChange={e => setFiltro("status", e.value)} options={STATUS_OPTIONS}
                            placeholder="Todos os status" showClear />
                    </Campo>
                    <Campo label="Solicitante">
                        <CustomDropdown value={filtros.solicitanteId} onChange={e => setFiltro("solicitanteId", e.value)} options={usuarios}
                            optionValue="id" optionLabel="nome" placeholder="Todos os solicitantes" showClear filter />
                    </Campo>
                </>
            )}
        >
            <CustomTable
                data={linhas}
                columns={COLUNAS}
                loading={loading}
                pagination
                pageSize={20}
                showHeader={false}
                className="text-[11px] border shadow-card rounded-xl"
                exportable
                exportFilename="dashboard_analise_limite_prazo"
                footer={() => null}
                onRowSelect={(row, e) => navegarComCtrl(navigate, `/solicitacoes/${row.solicitacaoId}`, e)}
            />
        </DashboardProcessoLayout>
    );
}
